import { X, Minus, Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { useCartStore } from "@public/stores/cartStore";
import { QuoteModal } from "@shared/components/Quote";
import { formatUnit } from "@shared/utils";

// Función para extraer texto plano de HTML
const stripHtmlTags = (html: string): string => {
  if (!html) return "";
  // Crear un elemento temporal para extraer el texto
  const tmp = document.createElement("div");
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || "";
};

interface CartProps {
  isOpen?: boolean;
  onClose?: () => void;
}

export function Cart({ onClose }: CartProps) {
  const {
    items,
    updateQuantity,
    removeFromCart,
    isEmpty,
    clearCart,
    closeCart,
    hasItemsWithZeroQuantity,
    isOpen: storeIsOpen,
  } = useCartStore();

  const [isQuoteModalOpen, setIsQuoteModalOpen] = useState(false);

  // Usar el estado del store en lugar de la prop
  const cartIsOpen = storeIsOpen;
  const cartIsEmpty = isEmpty();
  const hasZeroQuantityItems = hasItemsWithZeroQuantity();

  const handleQuantityChange = (itemId: string, newQuantity: number) => {
    const validQuantity = Math.max(0, newQuantity);
    updateQuantity(itemId, validQuantity);
  };

  const handleRemoveItem = (itemId: string) => {
    removeFromCart(itemId);
  };

  const handleClearCart = () => {
    clearCart();
  };

  const handleClose = () => {
    closeCart();
    onClose?.();
  };

  return (
    <>
      {/* Overlay para cerrar el carrito - mobile (más oscuro) */}
      <div
        className={`fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity duration-300 ${
          cartIsOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        } md:hidden`}
        onClick={handleClose}
      />

      {/* Overlay sutil para desktop */}
      <div
        className={`fixed inset-0 z-40 transition-opacity duration-300 ${
          cartIsOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        } hidden md:block`}
        onClick={handleClose}
      />

      {/* Sidebar del carrito */}
      <div
        className={`fixed top-0 right-0 h-full bg-white shadow-xl z-50 transition-transform duration-300 ease-in-out ${
          cartIsOpen ? "translate-x-0" : "translate-x-full"
        } w-full md:w-[40%] lg:w-[35%] flex flex-col`}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Tu cotización</h2>
          <div className="flex items-center space-x-2">
            {/* Botón temporal para limpiar carrito */}
            {!isEmpty && (
              <button
                onClick={handleClearCart}
                className="p-2 hover:bg-red-50 rounded-full transition-colors"
                title="Limpiar carrito"
              >
                <Trash2 className="h-4 w-4 text-red-500" />
              </button>
            )}
            <button
              onClick={handleClose}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <X className="h-5 w-5 text-gray-500" />
            </button>
          </div>
        </div>

        {/* Contenido */}
        <div className="flex-1 overflow-y-auto">
          {cartIsEmpty ? (
            // Estado vacío
            <div className="flex items-center justify-center h-full">
              <p className="text-gray-500 text-center">
                Tu carrito está vacío.
              </p>
            </div>
          ) : (
            // Lista de productos
            <div className="p-4 space-y-4">
              {items.map((item) => (
                <div
                  key={item._id}
                  className="bg-white border border-gray-200 rounded-lg p-3 relative"
                >
                  {/* Botón de eliminar */}
                  <button
                    onClick={() => handleRemoveItem(item._id)}
                    className="absolute top-2 right-2 p-1 text-gray-400 hover:text-red-500 transition-colors"
                    title="Eliminar producto"
                  >
                    <X className="h-4 w-4" />
                  </button>

                  <div className="flex items-center space-x-4 pr-6">
                    {/* Imagen del producto */}
                    <div className="flex-shrink-0">
                      {item.images && item.images[0]?.url ? (
                        <img
                          src={item.images[0].url}
                          alt={item.name}
                          className="w-20 h-14 rounded border-2 border-purple-200 object-cover"
                        />
                      ) : (
                        <div className="w-20 h-14 bg-gray-200 rounded border-2 border-purple-200 flex items-center justify-center">
                          <span className="text-gray-400 text-xs">
                            Sin imagen
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Información del producto */}
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-semibold text-gray-900 truncate">
                        {item.name}
                      </h3>
                      {item.brand && (
                        <p className="text-xs text-gray-400 mt-0.5">
                          {item.brand}
                        </p>
                      )}
                      {item.description && (
                        <p className="text-xs text-gray-500 mt-1 line-clamp-2">
                          {stripHtmlTags(item.description)}
                        </p>
                      )}
                    </div>

                    {/* Controles de cantidad */}
                    <div className="flex-shrink-0">
                      {(() => {
                        const unitText = formatUnit(item.unitOfMeasurement);
                        const isM2 = item.unitOfMeasurement === "m²" || item.unitOfMeasurement === "m2";
                        
                        if (isM2) {
                          // Input para metros cuadrados
                          return (
                            <div className="flex items-center space-x-2">
                              <input
                                type="number"
                                value={item.quantity}
                                onChange={(e) =>
                                  handleQuantityChange(
                                    item._id,
                                    Math.max(0, parseFloat(e.target.value) || 0)
                                  )
                                }
                                min="0"
                                className="w-20 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#282828] focus:border-[#282828] [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                placeholder="0"
                              />
                              <span className="px-2 py-1 text-xs border border-gray-300 rounded bg-gray-50 text-gray-600">
                                {unitText}
                              </span>
                            </div>
                          );
                        } else {
                          // Controles +/- para otras unidades
                          return (
                            <div className="flex flex-col items-end space-y-1">
                              <div className="flex items-center space-x-1">
                                <button
                                  onClick={() =>
                                    handleQuantityChange(item._id, item.quantity - 1)
                                  }
                                  className="w-7 h-7 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-50 transition-colors"
                                >
                                  <Minus className="h-3 w-3 text-gray-600" />
                                </button>
                                <input
                                  type="number"
                                  value={item.quantity}
                                  onChange={(e) =>
                                    handleQuantityChange(
                                      item._id,
                                      Math.max(0, parseInt(e.target.value) || 0)
                                    )
                                  }
                                  min="0"
                                  className="w-12 px-1 py-1 text-sm border border-gray-300 rounded text-center focus:outline-none focus:ring-2 focus:ring-[#282828] focus:border-[#282828] [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                  placeholder="0"
                                />
                                <button
                                  onClick={() =>
                                    handleQuantityChange(item._id, item.quantity + 1)
                                  }
                                  className="w-7 h-7 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-50 transition-colors"
                                >
                                  <Plus className="h-3 w-3 text-gray-600" />
                                </button>
                              </div>
                              <span className="text-xs text-gray-500">
                                {unitText}
                              </span>
                            </div>
                          );
                        }
                      })()}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer con botones */}
        <div className="border-t border-gray-200 p-4 space-y-3">
          {/* Mensaje de error si hay productos con cantidad 0 */}
          {hasZeroQuantityItems && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-sm text-red-600">
                ⚠️ Hay productos con cantidad 0. Por favor, ajusta las cantidades o elimina los productos antes de confirmar el presupuesto.
              </p>
            </div>
          )}
          <button
            onClick={() => setIsQuoteModalOpen(true)}
            disabled={cartIsEmpty || hasZeroQuantityItems}
            className="w-full bg-[#282828] text-white py-3 px-4 rounded-lg font-medium hover:bg-[#1a1a1a] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            CONFIRMAR PRESUPUESTO
          </button>
          <button
            onClick={handleClose}
            className="w-full bg-white text-gray-900 py-3 px-4 rounded-lg font-medium border border-gray-300 hover:bg-gray-50 transition-colors"
          >
            SEGUIR COMPRANDO
          </button>
        </div>
      </div>

      {/* Modal de cotización */}
      <QuoteModal
        isOpen={isQuoteModalOpen}
        onClose={() => setIsQuoteModalOpen(false)}
        cartItems={items}
      />
    </>
  );
}
